import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Diagnostic / inspection termites', price: 'Sur devis (déduit du traitement)' },
  { service: 'Traitement curatif maison (pièges-appâts ou barrière)', price: '1 000 € – 3 000 €' },
  { service: 'Traitement préventif avant construction', price: 'Sur devis selon surface' },
  { service: 'Traitement bois localisé (meuble, poutre)', price: 'À partir de 150 €' },
];

const FAQ_ITEMS = [
  {
    question: 'Comment savoir si c\'est bien des termites et pas des fourmis ailées ?',
    answer: 'Les termites ailés ont deux paires d\'ailes égales et une taille sans pincement ; les fourmis ont la taille marquée et des ailes inégales. Envoyez une photo sur WhatsApp : on vous répond en 2 h.',
  },
  {
    question: 'Combien coûte un traitement termites en Guadeloupe ?',
    answer: 'Entre 1 000 € et 3 000 € pour une maison en curatif, selon surface et méthode. Le préventif avant construction est bien moins cher que le curatif après infestation — c\'est l\'investissement le plus rentable en zone à haut risque.',
  },
  {
    question: 'Les pièges ou la barrière chimique : c\'est quoi le mieux ?',
    answer: 'Les pièges-appâts éliminent la colonie entière sans travaux, idéal en curatif. La barrière protège durablement le bâtiment, idéale en préventif ou en complément. Notre technicien recommande selon votre cas — pas selon ce qui est le plus cher.',
  },
  {
    question: 'Une maison en béton risque-t-elle quelque chose ?',
    answer: 'Oui : les termites passent par les microfissures et gaines pour atteindre charpente, menuiseries, plinthes et meubles. Le béton ne protège que le béton.',
  },
  {
    question: 'Combien de temps dure la protection ?',
    answer: 'Plusieurs années, avec visites de contrôle prévues dans la garantie de traitement. Les stations d\'appâtage se maintiennent en continu.',
  },
  {
    question: 'Je vends ma maison, que dois-je faire ?',
    answer: 'Dans les communes sous arrêté, un état termites de moins de 6 mois est exigé à la vente. S\'il est positif, un traitement documenté rassure l\'acheteur et protège le prix. On vous accompagne sur les deux volets.',
  },
];

export default function Termites() {
  useDocumentHead({
    title: 'Traitement termites Guadeloupe : curatif & préventif certifié',
    description: 'Termites en Guadeloupe ? Diagnostic et traitement curatif ou préventif. Zone à haut risque : agissez avant que la charpente ne cède. Devis gratuit.',
    jsonLd: [service({ name: 'Traitement des termites', serviceType: 'Traitement des termites', path: '/traitement-termites-guadeloupe', description: 'Diagnostic et traitement curatif ou préventif des termites en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Termites', path: '/traitement-termites-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Termites'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/termites-1.webp"
            alt="Technicien inspectant les dégâts de termites sur une poutre de charpente dans une maison ancienne en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Traitement des termites en Guadeloupe : agissez avant qu&rsquo;ils ne mangent la maison
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              La Guadeloupe est officiellement classée territoire à haut risque termites. Dans les DOM, les dégâts de termites sont la deuxième cause de sinistre dans l&rsquo;habitat après les catastrophes naturelles — une charpente peut être condamnée en quelques années, et des maisons de moins d&rsquo;un an se font infester. Le pire : les termites travaillent en silence, de l&rsquo;intérieur du bois. Quand on les voit, c&rsquo;est déjà grave. Nos techniciens diagnostiquent et traitent, en curatif comme en préventif.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* LES TERMITES DE GUADELOUPE */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Les termites de Guadeloupe : deux ennemis différents
                </h2>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    <strong>Les termites souterrains</strong> (Heterotermes, Nasutitermes, Coptotermes) vivent dans le sol et montent dans les constructions par des galeries-tunnels (cordonnets de terre le long des murs et fondations — le signe qui doit alerter). Ils attaquent charpentes, ossatures, plinthes, encadrements.
                  </p>
                  <p>
                    <strong>Les termites de bois sec</strong> (Cryptotermes brevis) vivent directement dans le bois attaqué, sans contact avec le sol : meubles, portes, poutres — on les repère à leurs petites crottes granuleuses (vermoulure) qui s&rsquo;accumulent en petits tas. Climat chaud et humide toute l&rsquo;année = activité permanente, accélérée en saison des pluies.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/termites-2.webp"
                    alt="Gros plan sur les galeries creusées par les termites dans une poutre de bois ancien en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* LES SIGNES D'UNE INFESTATION */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Les signes d&rsquo;une infestation
              </h2>
              <ul className="space-y-3 text-foreground-600 text-base leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Cordonnets de terre le long des murs, fondations ou canalisations</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Bois qui sonne creux quand on le frappe</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Petits tas de granulés (crottes de termites de bois sec) sous les meubles ou poutres</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Ailes abandonnées près des fenêtres (essaimage des termites ailés, souvent en début de saison des pluies)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Peinture ou surface du bois qui cloque</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-secondary-500 text-lg w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                  <span>Portes et fenêtres qui coincent (le bois travaille)</span>
                </li>
              </ul>
              <p className="text-foreground-700 font-semibold mt-5 text-base">
                Un seul de ces signes justifie un diagnostic.
              </p>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOS TRAITEMENTS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-10 text-center">
              Nos traitements
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-10">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  Pièges-appâts qui contaminent la colonie entière, barrière chimique pour une protection durable, ou traitement du bois localisé : chaque situation demande la bonne méthode. Nos techniciens recommandent la solution adaptée à votre cas, pas la plus chère.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/termites-3.webp"
                    alt="Installation d'une station anti-termites autour des fondations d'une maison créole en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Curatif */}
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-medicine-bottle-line text-secondary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                  Curatif (la maison est infestée)
                </h3>
                <ul className="space-y-3 text-foreground-600 text-sm leading-relaxed">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-secondary-500 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span><strong>Pièges-appâts</strong> : stations posées autour et dans la maison ; les termites consomment l&rsquo;appât et contaminent la colonie jusqu&rsquo;à son effondrement. La méthode de référence, sans travaux lourds.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-secondary-500 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span><strong>Barrière chimique</strong> : injection dans les sols et murs (forages discrets) créant une zone de protection infranchissable.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-secondary-500 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span><strong>Traitement du bois</strong> : injection et pulvérisation des pièces attaquées encore saines.</span>
                  </li>
                </ul>
              </div>

              {/* Préventif */}
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-mango-500/20">
                  <i className="ri-shield-check-line text-mango-500 text-xl w-6 h-6 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-bold text-xl text-foreground-950 mb-4">
                  Préventif (avant construction ou pour protéger un bien sain)
                </h3>
                <ul className="space-y-3 text-foreground-600 text-sm leading-relaxed">
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-secondary-500 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span><strong>Barrière physico-chimique</strong> sous dallage et en périphérie (obligatoire de fait en zone à haut risque pour construire serein).</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-secondary-500 w-5 h-5 flex items-center justify-center mt-0.5 shrink-0"></i>
                    <span><strong>Traitement préventif</strong> des bois de charpente.</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="max-w-3xl mx-auto mt-6">
              <div className="bg-secondary-100/50 rounded-xl p-5 border border-secondary-200/50 text-center">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Garantie de traitement pluriannuelle</strong> avec visites de contrôle — les détails sont fixés au devis.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* LA RÉGLEMENTATION */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                La réglementation (et pourquoi elle vous concerne)
              </h2>
              <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                <p>
                  La loi du 8 juin 1999 impose de déclarer en mairie tout foyer de termites constaté. Dans les communes sous arrêté préfectoral, un état relatif à la présence de termites est exigé lors de la vente d&rsquo;un bien.
                </p>
                <p>
                  Si vous vendez, achetez ou rénovez en Guadeloupe, la question termites n&rsquo;est pas optionnelle — et un diagnostic positif non traité fait chuter la valeur du bien. Nos techniciens interviennent après diagnostic positif comme en prévention avant travaux.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de traitement termites
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <p className="text-center text-foreground-500 text-sm mt-4">
                Chaque maison est différente (surface, type de termite, ampleur) : le prix exact est fixé après inspection, gratuitement et sans engagement.
              </p>
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur les termites
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Un doute sur votre charpente ? N'attendez pas que le bois sonne creux."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement désinsectisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tous nos traitements contre les insectes
              </span>
            </Link>
            <Link
              to="/"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Retour à l&rsquo;accueil
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Découvrez tous nos services de lutte contre les nuisibles
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="termites" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}